import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PlusIcon, WrenchIcon } from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import EmptyState from "../../components/EmptyState";
import SkeletonRows from "../../components/SkeletonRows";
import {
  ErrorBanner,
  Field,
  Modal,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextArea,
} from "../../components/crm/Form";
import StatusBadge from "../../components/ops/StatusBadge";
import { useAuth } from "../../contexts/AuthContext";
import api, { formatApiError } from "../../lib/api";

const STATUSES = [
  "draft",
  "scheduled",
  "assigned",
  "en_route",
  "on_site",
  "paused",
  "completed",
  "canceled",
];
const optsFrom = (arr) =>
  arr.map((v) => ({ value: v, label: v.replace(/_/g, " ") }));

const OFFICE = ["owner", "ops"];

export default function WorkOrdersList() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";

  const [list, setList] = useState([]);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const isOffice = OFFICE.includes(user?.role);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const filters = {};
      if (status) filters.status = status;
      const { data } = await api.get("/work-orders/", { params: filters });
      setList(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  return (
    <Layout title="Work Orders" subtitle="Dispatch">
      <EntityHeader
        overline="dispatch"
        title="Work Orders"
        subtitle={
          isOffice
            ? "All scheduled and in-progress jobs."
            : "Work orders assigned to you."
        }
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="w-44">
          <Select
            value={status}
            onChange={(v) => setParam("status", v)}
            options={optsFrom(STATUSES)}
            placeholder="All statuses"
            data-testid="wo-status-filter"
          />
        </div>
        {isOffice && (
          <PrimaryButton
            onClick={() => setOpen(true)}
            data-testid="wo-create-button"
          >
            <PlusIcon size={14} weight="bold" /> New Work Order
          </PrimaryButton>
        )}
      </div>

      <ErrorBanner message={err} />

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="wo-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-600">
              <th className="text-left px-5 py-3 font-bold">Property</th>
              <th className="text-left px-5 py-3 font-bold">Customer</th>
              <th className="text-left px-5 py-3 font-bold">Status</th>
              <th className="text-left px-5 py-3 font-bold">Scheduled</th>
              <th className="text-left px-5 py-3 font-bold">Lead Tech</th>
              <th className="text-left px-5 py-3 font-bold">Assistant</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && list.length === 0 && <SkeletonRows count={5} cols={6} />}
            {!loading && list.length === 0 && (
              <EmptyState
                inTable
                colSpan={6}
                icon={WrenchIcon}
                title={isOffice ? "No work orders yet" : "No work orders assigned"}
                description={
                  isOffice
                    ? "Work orders are created when a service request is dispatched. Open a service request and click 'Convert to Work Order'."
                    : "Once an office user assigns you to a job, it'll show up here. Check back shortly."
                }
                testId="wo-empty"
              />
            )}
            {list.map((w) => (
              <tr
                key={w.id}
                className="cursor-pointer hover:bg-slate-50/60"
                onClick={() => nav(`/work-orders/${w.id}`)}
                data-testid={`wo-row-${w.id}`}
              >
                <td className="px-5 py-3 font-medium text-slate-900 max-w-md truncate">
                  {w.property_address || "—"}
                </td>
                <td className="px-5 py-3 text-xs text-slate-700">
                  {w.customer_name || "—"}
                </td>
                <td className="px-5 py-3">
                  <StatusBadge value={w.status} />
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-700 tabular">
                  {w.scheduled_start
                    ? new Date(w.scheduled_start).toLocaleString()
                    : "—"}
                </td>
                <td className="px-5 py-3 text-xs text-slate-700">
                  {w.assigned_lead_name || "—"}
                </td>
                <td className="px-5 py-3 text-xs text-slate-700">
                  {w.assigned_assistant_name || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isOffice && (
        <CreateWorkOrderModal
          open={open}
          onClose={() => setOpen(false)}
          onCreated={(w) => {
            setOpen(false);
            nav(`/work-orders/${w.id}`);
          }}
        />
      )}
    </Layout>
  );
}

function CreateWorkOrderModal({ open, onClose, onCreated }) {
  const empty = useMemo(
    () => ({
      service_request_id: "",
      customer_id: "",
      property_id: "",
      solar_system_id: "",
      scheduled_start: "",
      scheduled_end: "",
      dispatch_notes: "",
    }),
    []
  );
  const [form, setForm] = useState(empty);
  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [systems, setSystems] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(empty);
    setErr("");
    api
      .get("/customers/")
      .then((r) => setCustomers(r.data))
      .catch(() => {});
  }, [open, empty]);

  useEffect(() => {
    if (!form.customer_id) {
      setProperties([]);
      return;
    }
    api
      .get("/properties/", { params: { customer_id: form.customer_id } })
      .then((r) => setProperties(r.data))
      .catch(() => {});
  }, [form.customer_id]);

  useEffect(() => {
    if (!form.property_id) {
      setSystems([]);
      return;
    }
    api
      .get("/solar-systems/", { params: { property_id: form.property_id } })
      .then((r) => setSystems(r.data))
      .catch(() => {});
  }, [form.property_id]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        property_id: form.property_id,
      };
      if (form.solar_system_id) payload.solar_system_id = form.solar_system_id;
      if (form.scheduled_start) payload.scheduled_start = form.scheduled_start;
      if (form.scheduled_end) payload.scheduled_end = form.scheduled_end;
      if (form.dispatch_notes) payload.dispatch_notes = form.dispatch_notes;
      const { data } = await api.post("/work-orders/", payload);
      onCreated(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Work Order"
      subtitle="ad-hoc dispatch"
      data-testid="wo-create-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Customer" required>
          <Select
            value={form.customer_id}
            onChange={(v) =>
              setForm({ ...form, customer_id: v || "", property_id: "" })
            }
            options={customers.map((c) => ({ value: c.id, label: c.name }))}
            placeholder="Select customer…"
            data-testid="wo-customer-select"
          />
        </Field>
        <Field label="Property" required>
          <Select
            value={form.property_id}
            onChange={(v) => setForm({ ...form, property_id: v || "" })}
            options={properties.map((p) => ({
              value: p.id,
              label: p.address,
            }))}
            placeholder={
              form.customer_id ? "Select property…" : "Pick customer first"
            }
            disabled={!form.customer_id}
            data-testid="wo-property-select"
          />
        </Field>
        <Field label="Solar System">
          <Select
            value={form.solar_system_id}
            onChange={(v) => setForm({ ...form, solar_system_id: v || "" })}
            options={systems.map((s) => ({
              value: s.id,
              label: `${s.install_year} · ${s.original_installer || "system"}`,
            }))}
            placeholder={form.property_id ? "None" : "Pick property first"}
            disabled={!form.property_id}
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Scheduled Start">
            <input
              type="datetime-local"
              value={form.scheduled_start}
              onChange={(e) =>
                setForm({ ...form, scheduled_start: e.target.value })
              }
              data-testid="wo-scheduled-start"
              className="w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
            />
          </Field>
          <Field label="Scheduled End">
            <input
              type="datetime-local"
              value={form.scheduled_end}
              onChange={(e) =>
                setForm({ ...form, scheduled_end: e.target.value })
              }
              data-testid="wo-scheduled-end"
              className="w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
            />
          </Field>
        </div>
        <Field label="Dispatch Notes">
          <TextArea
            value={form.dispatch_notes}
            onChange={(v) => setForm({ ...form, dispatch_notes: v })}
            data-testid="wo-dispatch-notes"
          />
        </Field>
        <ErrorBanner message={err} />
        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={busy}
            data-testid="wo-submit"
          >
            {busy ? "Creating…" : "Create Work Order"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}
